import CampaignIcon from '@mui/icons-material/Campaign';
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography,
  capitalize,
} from '@mui/material';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { RequireSignInLevel, useElevationSignInCheck } from '../abelcommon/hooks/useElevationSignInCheck';
import { authState, env, globalAlertState, userStorageKey } from '../api';
import BarDrawerLayout, { useLayoutState } from '../components/BarDrawerLayout';
import { XBox } from '../components/CenterBox';
import Navigator from './Navigator';
import LanguageIcon from '@mui/icons-material/Language';
import { useTranslation } from 'react-i18next';
import { loadBalancerAPI } from '../api/load-balancer';
import { CircularProgress } from '@mui/material';

export function Layout({
  requireSignIn = true,
  requireSignInLevel,
}: {
  requireSignIn?: boolean;
  requireSignInLevel?: RequireSignInLevel;
}) {
  const drawerWidth = 256;
  return (
    <BarDrawerLayout
      breakpoint={'md'}
      barOptions={{
        desktopHeight: 60,
        mobileHeight: 48,
      }}
      desktopDrawerOptions={{
        mode: 'permanent',
        width: drawerWidth,
      }}
      mobileDrawerOptions={{
        width: drawerWidth,
      }}
      header={<Header />}
      sideNav={<Navigator />}
      footer={<Divider sx={{ mx: 2, mb: 3 }} />}
      main={<Main requireSignIn={requireSignIn} requireSignInLevel={requireSignInLevel} />}
    />
  );
}

function useMiningFee() {
  const [miningShow, setMiningShow] = useState(window.innerWidth > 500);
  useLayoutEffect(() => {
    function updateShow() {
      setMiningShow(window.innerWidth > 500);
    }
    window.addEventListener('resize', updateShow);
    updateShow();
    return () => window.removeEventListener('resize', updateShow);
  });
  return miningShow;
}

function useLanguage() {
  const [languageShow, setLanguageShow] = useState(window.innerWidth > 300);
  useLayoutEffect(() => {
    function updateShow() {
      setLanguageShow(window.innerWidth > 300);
    }
    window.addEventListener('resize', updateShow);
    updateShow();
    return () => window.removeEventListener('resize', updateShow);
  });
  return languageShow;
}

function Header() {
  const [layoutState] = useLayoutState();
  const { t, i18n } = useTranslation();
  const [authStateValue, setAuthState] = useRecoilState(authState);
  const [language, setLanguage] = useState<string | undefined>(undefined);
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);

  const {
    data: allLanguageData,
    isLoading: allLanguageLoading,
    error: allLanguageError,
  } = loadBalancerAPI.hookWrapperQuery.useGetAllLanguage();

  useEffect(() => {
    const isContentManagementPage = location.pathname.includes('content-management');
    if (isContentManagementPage) {
      setLanguage(authStateValue?.user?.languageCode || i18n.language);
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang');
    const currentPath = window.location.pathname;
    const initLanguage = langParam || authStateValue?.user?.languageCode || i18n.language;

    if (authStateValue?.user?.languageCode && langParam) {
      const validLanguages = allLanguageData?.map((language) => language.code);

      if (!allLanguageLoading && !validLanguages?.includes(langParam)) {
        setLanguage(authStateValue?.user?.languageCode || i18n.language);
        i18n.changeLanguage(authStateValue?.user?.languageCode || i18n.language);
        navigate(`${currentPath}`);
        setError(`Not Support Language: ${langParam}`);
        return;
      }
    }

    const updateLanguage = async () => {
      if (authStateValue?.user?.languageCode && langParam && langParam !== authStateValue.user.languageCode) {
        try {
          const response = await loadBalancerAPI.apiInstance.changeLanguageUser(langParam);
          if (response.status === 200) {
            const savedValue = localStorage.getItem(userStorageKey);
            if (savedValue) {
              try {
                const parsedValue = JSON.parse(savedValue);
                if (parsedValue?.refreshToken) {
                  const { data } = await loadBalancerAPI.apiInstance.refreshTokenWithUserInfo(parsedValue.refreshToken);
                  setAuthState(data.data);
                }
              } catch (error) {
                console.error('Error refreshing token', error);
              }
            }
            navigate(`${currentPath}?lang=${langParam}`);
          } else {
            console.error('Failed to change language');
          }
        } catch (error) {
          console.error('Error changing language', error);
        }
      } else {
        setLanguage(initLanguage);
        i18n.changeLanguage(initLanguage);
      }
    };
    updateLanguage();
  }, [authStateValue, i18n, location.pathname, navigate, setAuthState, allLanguageData, allLanguageLoading]);

  const handleClose = () => {
    setError(null);
  };

  const handleChangeLanguage = async (event: any) => {
    const newLanguage = event.target.value;
    const currentPath = window.location.pathname;
    if (!authStateValue?.user?.languageCode) {
      i18n.changeLanguage(newLanguage);
      setLanguage(newLanguage);
      navigate(`${currentPath}?lang=${newLanguage}`);
    } else {
      try {
        const response = await loadBalancerAPI.apiInstance.changeLanguageUser(newLanguage);
        i18n.changeLanguage(newLanguage);
        setLanguage(newLanguage);
        if (response.status === 200) {
          const savedValue = localStorage.getItem(userStorageKey);
          if (savedValue) {
            try {
              const parsedValue = JSON.parse(savedValue);
              if (!parsedValue?.refreshToken) return;
              const { data } = await loadBalancerAPI.apiInstance.refreshTokenWithUserInfo(parsedValue.refreshToken);
              setAuthState(data.data);
            } catch (error) {
              console.error('Error refreshing token', error);
            }
          }
          navigate(`${currentPath}?lang=${newLanguage}`);
        } else {
          console.error('Failed to change language');
        }
      } catch (error) {
        console.error('Error changing language', error);
      }
    }
  };

  const showMiningFeeBox = useMiningFee();
  const showLanguageBox = useLanguage();
  return (
    <>
      <Box
        sx={{
          width: 1,
          height: 1,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Stack sx={{ flexDirection: 'row', alignItems: 'center', width: '100%', height: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Box component={'img'} src={'abel-icon-blue.png'} sx={{ height: 36, width: 36, mx: 1 }} />
            {layoutState.isDesktop && (
              <Stack
                direction={layoutState.isDesktop ? 'row' : 'column'}
                spacing={layoutState.isDesktop ? 1 : 0}
                sx={{ width: 1, alignItems: 'center' }}
              >
                <Link to="/" style={{ textDecoration: 'none' }}>
                  <Typography variant={'h4'} sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {'Maxpool ' ?? `${capitalize(env.poolServiceName)} ${t('pool')}`}
                  </Typography>
                </Link>
              </Stack>
            )}
          </Box>
          {showMiningFeeBox ? (
            <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center' }}>
              <Chip
                color="info"
                size="medium"
                variant="outlined"
                sx={{}}
                label={
                  <Stack
                    sx={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 1,
                      fontWeight: 500,
                    }}
                  >
                    <CampaignIcon />
                    <Box
                      sx={{
                        overflow: 'hidden',
                      }}
                    >
                      <Box>
                        {/* Fee rate: 5%--less than 500 cards; 3%--more than 500 cards */}
                        {t('layoutPage.miningFee')}: 1%
                      </Box>
                    </Box>
                  </Stack>
                }
              />
            </Box>
          ) : null}
          {showLanguageBox ? (
            <Box sx={{ display: 'flex', alignItems: 'center', mx: 2 }}>
              {allLanguageError ? (
                <Typography color="error">{t('layoutPage.failedToLoadLanguage')}</Typography>
              ) : allLanguageLoading ? (
                <CircularProgress />
              ) : (
                <FormControl>
                  <Select
                    labelId="language-select-label"
                    value={language}
                    onChange={handleChangeLanguage}
                    input={
                      <OutlinedInput
                        startAdornment={
                          <InputAdornment position="start">
                            <LanguageIcon />
                          </InputAdornment>
                        }
                      />
                    }
                    sx={{
                      minWidth: 120,
                      '.MuiSelect-select': {
                        padding: '5px 14px',
                        fontSize: '16px',
                        fontWeight: 500,
                      },
                    }}
                  >
                    {allLanguageData?.map((lang) => (
                      <MenuItem key={lang.code} value={lang.code}>
                        {lang.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          ) : null}
        </Stack>
      </Box>
      {!allLanguageLoading && error && (
        <Dialog open={Boolean(error)} onClose={handleClose}>
          <DialogTitle>Error</DialogTitle>
          <DialogContent>{error}</DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="error">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}

function Main({
  requireSignIn = true,
  requireSignInLevel,
}: {
  requireSignIn?: boolean;
  requireSignInLevel?: RequireSignInLevel;
}) {
  const navigate = useNavigate();
  const [, setAlertState] = useRecoilState(globalAlertState);
  const {
    status: signedInStatus,
    highestLevel,
    isLoading,
  } = useElevationSignInCheck(requireSignInLevel ?? RequireSignInLevel.User);

  const signInStatusLevelMessageMap = useMemo(
    () => ({
      [RequireSignInLevel.User]: 'Please sign in to access this page.',
      [RequireSignInLevel.MiningAccount]:
        "Can't access this page because you haven't select any account or something went wrong. Please try again.",
    }),
    []
  );

  useEffect(() => {
    if (requireSignIn && !signedInStatus && !isLoading) {
      setAlertState((prevState) => ({
        ...prevState,
        isOpen: true,
        severity: 'error',
        message: "Can't signed in. Please try again later.",
      }));
      navigate('/');
    }
  }, [requireSignIn, signedInStatus, isLoading, highestLevel, signInStatusLevelMessageMap, setAlertState, navigate]);

  if (requireSignIn && !signedInStatus) {
    const message = signInStatusLevelMessageMap[highestLevel];
    return (
      <XBox sx={{ minHeight: 300, px: 2, textAlign: 'center' }}>
        <Typography variant={'h6'}>{message}</Typography>
      </XBox>
    );
  } else {
    return (
      <Container maxWidth={'xl'} sx={{ p: { xs: 3, md: 4 } }}>
        <Outlet />
      </Container>
    );
  }
}

export default Layout;
